// TrackCharts — track-first visual layer.
// 1. Activity heatmap: rows × weeks, cell = visibility.
// 2. Comparative score chart: selected signal vs core / watchlist averages.
// 3. Role distribution + macro theme map (clickable filter).

import React, { useMemo } from "react";
import { roleLabel, roleTone, visibilityTone, buildComparisonSeries, buildMaturityInterpretation } from "../../lib/tracks";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const visibilityCell = (vis) => {
    const v = (vis || "").toUpperCase();
    if (v === "SIGNAL") return "bg-[hsl(var(--ink))] text-white";
    if (v === "WATCHLIST") return "bg-[hsl(var(--warning))] text-white";
    if (v === "NOISE") return "bg-[hsl(var(--surface-sunken))] text-[hsl(var(--mute))] border border-[hsl(var(--line))]";
    return "bg-transparent text-[hsl(var(--mute))] border border-[hsl(var(--line))]";
};

const ActivityHeatmap = ({ tracks, historyByTrackId, allWeeks, onSelectTrack }) => {
    if (!tracks.length || !allWeeks.length) return null;
    return (
        <div className="card-flat" data-testid="track-activity-heatmap">
            <header className="px-5 py-4 border-b border-[hsl(var(--line))]">
                <div className="eyebrow">Signal activity over time</div>
                <h3 className="display text-xl mt-1">Where structural evidence has appeared</h3>
                <p className="text-xs text-[hsl(var(--mute))] mt-1.5">Each cell shows the signal's visibility in that week. Filled cells = sustained presence in market evidence.</p>
            </header>
            <div className="overflow-x-auto">
                <table className="text-[11px]">
                    <thead>
                        <tr>
                            <th className="px-3 py-2 text-left text-[hsl(var(--mute))] uppercase tracking-wider sticky left-0 bg-[hsl(var(--surface))]">Signal</th>
                            {allWeeks.map((w) => (
                                <th key={w} className="px-1.5 py-2 text-[9px] text-[hsl(var(--mute))] num text-center min-w-[40px]" title={w}>{w.replace("2026-W", "W")}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tracks.map((t) => {
                            const hist = historyByTrackId[t.TrackID] || [];
                            const byYW = {};
                            for (const h of hist) byYW[h.YearWeek] = h;
                            return (
                                <tr key={t.TrackID} className="border-t border-[hsl(var(--line))]">
                                    <td className="px-3 py-1.5 sticky left-0 bg-[hsl(var(--surface))] max-w-[220px]">
                                        <button type="button" onClick={() => onSelectTrack && onSelectTrack(t.TrackID)} className="text-left text-[hsl(var(--ink))] hover:text-[hsl(var(--accent-ink))] hover:underline truncate block max-w-[210px]" title={t.StableTrackName}>{t.StableTrackName}</button>
                                    </td>
                                    {allWeeks.map((w) => {
                                        const h = byYW[w];
                                        const score = h ? h.Score : null;
                                        return (
                                            <td key={w} className="px-1.5 py-1.5 text-center">
                                                {h ? (
                                                    <div className={`mx-auto w-7 h-5 rounded-sm flex items-center justify-center text-[9px] font-medium num ${visibilityCell(h.Visibility)}`} title={`${w} · ${h.Visibility} · score ${score}`}>
                                                        {score ?? ""}
                                                    </div>
                                                ) : (
                                                    <div className="mx-auto w-7 h-5 rounded-sm border border-dashed border-[hsl(var(--line))]" title={`${w} · not observed`} />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-5 py-3 border-t border-[hsl(var(--line))] flex items-center gap-3 text-[11px] text-[hsl(var(--mute))]">
                <span>Legend:</span>
                <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[hsl(var(--ink))]" />Signal</span>
                <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[hsl(var(--warning))]" />Watchlist</span>
                <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm border border-[hsl(var(--line))]" />Noise / not observed</span>
            </div>
        </div>
    );
};

// --- Comparative score chart -------------------------------------------------

const ComparativeScoreChart = ({ tracks, historyByTrackId, selectedTrack }) => {
    const series = useMemo(
        () => buildComparisonSeries({ tracks, historyByTrackId, selectedTrack }),
        [tracks, historyByTrackId, selectedTrack]
    );

    const lines = useMemo(() => {
        if (!series) return [];
        const out = [];
        if (series.selected) {
            out.push({
                id: "selected",
                label: series.selected.label,
                points: series.selected.points,
                stroke: "hsl(var(--accent))",
                strokeWidth: 2.4,
                dashed: false,
                emphasis: true,
            });
        }
        if (series.coreAvg) {
            out.push({
                id: "coreAvg",
                label: series.coreAvg.label,
                points: series.coreAvg.points,
                stroke: "hsl(var(--ink))",
                strokeWidth: 1.5,
                dashed: false,
                emphasis: false,
            });
        }
        if (series.watchlistAvg) {
            out.push({
                id: "watchlistAvg",
                label: series.watchlistAvg.label,
                points: series.watchlistAvg.points,
                stroke: "hsl(var(--warning))",
                strokeWidth: 1.5,
                dashed: true,
                emphasis: false,
            });
        }
        return out;
    }, [series]);

    if (!series || !lines.length) return null;

    const W = 720;
    const H = 180;
    const PAD_X = 24;
    const PAD_TOP = 12;
    const PAD_BOTTOM = 24;

    const ys = lines.flatMap((l) => l.points.map((p) => p.y).filter((v) => typeof v === "number"));
    if (!ys.length) return null;
    const minY = Math.max(0, Math.min(...ys) - 5);
    const maxY = Math.max(...ys) + 5;
    const xs = series.allWeeks.map((w) => w.weekIndex);
    const xMin = xs[0];
    const xMax = xs[xs.length - 1];
    const xSpan = Math.max(1, xMax - xMin);
    const ySpan = Math.max(1, maxY - minY);
    const sx = (x) => PAD_X + ((x - xMin) / xSpan) * (W - 2 * PAD_X);
    const sy = (y) => H - PAD_BOTTOM - ((y - minY) / ySpan) * (H - PAD_TOP - PAD_BOTTOM);

    // Build path strings; gap on null points by closing/restarting subpath.
    const pathFor = (points) => {
        let d = "";
        let pen = false;
        for (const p of points) {
            if (typeof p.y !== "number") {
                pen = false;
                continue;
            }
            d += `${pen ? "L" : "M"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)} `;
            pen = true;
        }
        return d.trim();
    };

    return (
        <div className="card-flat p-5" data-testid="comparative-score-chart">
            <div className="eyebrow">Signal strength over time</div>
            <h3 className="display text-lg mt-1 leading-snug">{selectedTrack?.StableTrackName || "Comparison view"}</h3>
            <p className="text-xs text-[hsl(var(--mute))] mt-1.5">Selected signal compared with core and watchlist signal averages, week by week.</p>

            <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-3" preserveAspectRatio="none" role="img" aria-label="Signal strength over time">
                {/* Y axis lines */}
                <line x1={PAD_X} y1={H - PAD_BOTTOM} x2={W - PAD_X} y2={H - PAD_BOTTOM} stroke="hsl(var(--line))" strokeWidth="1" />
                <line x1={PAD_X} y1={PAD_TOP} x2={W - PAD_X} y2={PAD_TOP} stroke="hsl(var(--line))" strokeWidth="0.5" strokeDasharray="2 3" />

                {/* Lines (averages first, selected last so it sits on top) */}
                {[...lines].sort((a, b) => (a.emphasis ? 1 : 0) - (b.emphasis ? 1 : 0)).map((l) => (
                    <path
                        key={l.id}
                        d={pathFor(l.points)}
                        fill="none"
                        stroke={l.stroke}
                        strokeWidth={l.strokeWidth}
                        strokeDasharray={l.dashed ? "4 4" : undefined}
                        opacity={l.emphasis ? 1 : 0.85}
                    />
                ))}

                {/* Dots only on the selected line for readability */}
                {series.selected && series.selected.points.filter((p) => typeof p.y === "number").map((p, i) => (
                    <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r="2.6" fill="hsl(var(--accent))">
                        <title>{p.label} · score {p.y}</title>
                    </circle>
                ))}

                {/* X axis labels: first / mid / last for compactness */}
                {[series.allWeeks[0], series.allWeeks[Math.floor(series.allWeeks.length / 2)], series.allWeeks[series.allWeeks.length - 1]]
                    .filter(Boolean)
                    .map((w, idx, arr) => (
                        <text
                            key={`${w.weekIndex}-${idx}`}
                            x={sx(w.weekIndex)}
                            y={H - 6}
                            fontSize="9"
                            fill="hsl(var(--mute))"
                            textAnchor={idx === 0 ? "start" : idx === arr.length - 1 ? "end" : "middle"}
                        >{w.yearWeek}</text>
                    ))}
            </svg>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[hsl(var(--ink-soft))] mt-3" data-testid="comparative-legend">
                {lines.map((l) => (
                    <span key={l.id} className="inline-flex items-center gap-1.5">
                        <span
                            className="inline-block w-4 h-[2px]"
                            style={{ backgroundColor: l.stroke, borderTop: l.dashed ? "2px dashed " + l.stroke : undefined }}
                        />
                        <span className={l.emphasis ? "font-medium text-[hsl(var(--ink))]" : undefined}>{l.label}</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

// --- Role distribution with interpretation -----------------------------------

const RoleDistribution = ({ summary, tracks }) => {
    const interpretation = useMemo(
        () => buildMaturityInterpretation({ summary, tracks }),
        [summary, tracks]
    );
    if (!summary) return null;

    // Detect governed dataset so we can swap "Exploratory / uncurated" for
    // the governed "Weekly movements" bucket. Falls back to legacy labels.
    let governedWeekly = 0; let governedSeen = false;
    for (const t of (tracks || [])) {
        if (t && t._governed) {
            governedSeen = true;
            if (t._governed.isWeeklyDynamic) governedWeekly += 1;
        }
    }
    const buckets = governedSeen
        ? [
              { label: "Core signals", value: summary.coreTracks || 0, tone: "ink" },
              { label: "Watchlist signals", value: summary.watchlistTracks || 0, tone: "warning" },
              { label: "Sub-patterns", value: summary.subPatterns || 0, tone: "muted" },
              { label: "Weekly movements", value: governedWeekly, tone: "muted" },
          ]
        : [
              { label: "Core signals", value: summary.coreTracks || 0, tone: "ink" },
              { label: "Watchlist signals", value: summary.watchlistTracks || 0, tone: "warning" },
              { label: "Sub-patterns", value: summary.subPatterns || 0, tone: "muted" },
              { label: "Exploratory / uncurated", value: (summary.exploratoryClusters || 0) + (summary.uncurated || 0), tone: "muted" },
          ];
    const max = Math.max(1, ...buckets.map((b) => b.value));
    return (
        <div className="card-flat p-5" data-testid="role-distribution">
            <div className="eyebrow">Market signal maturity</div>
            <h3 className="display text-lg mt-1">Where structure has been confirmed</h3>
            <ul className="mt-4 space-y-2.5">
                {buckets.map((b) => (
                    <li key={b.label} className="flex items-center gap-3">
                        <span className="text-xs text-[hsl(var(--ink-soft))] w-44 flex-none">{b.label}</span>
                        <div className="flex-1 h-2 bg-[hsl(var(--surface-sunken))] rounded-sm overflow-hidden">
                            <div className={`h-full ${b.tone === "ink" ? "bg-[hsl(var(--ink))]" : b.tone === "warning" ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--mute))]"}`} style={{ width: `${(b.value / max) * 100}%` }} />
                        </div>
                        <span className="text-sm font-semibold num text-[hsl(var(--ink))] w-8 text-right">{b.value}</span>
                    </li>
                ))}
            </ul>
            {interpretation.length > 0 && (
                <div className="mt-5 border-t border-[hsl(var(--line))] pt-4 space-y-2 text-xs leading-relaxed text-[hsl(var(--ink-soft))]" data-testid="maturity-interpretation">
                    {interpretation.map((line, i) => (
                        <p key={i} className={i === 0 ? "text-[hsl(var(--ink))] font-medium" : undefined}>{line}</p>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- Macro Theme Map extracted to ./MacroThemeMap.jsx ----------------------

const TrackCharts = ({ tracks, historyByTrackId, allWeeks, summary, selectedTrack, onSelectTrack }) => {
    // Heatmap rows are sorted by persistence ratio descending so the
    // most durable signals appear first (e.g. 24/24 ranks above 23/24).
    // Tie-breakers, in order: weeks observed desc, latest score desc,
    // then name asc — yielding a stable, predictable order.
    const heatmapTracks = useMemo(() => {
        return tracks
            .filter((t) => t._isPrimary)
            .slice()
            .sort((a, b) => {
                const ar = typeof a.PersistenceRatio === "number" ? a.PersistenceRatio : null;
                const br = typeof b.PersistenceRatio === "number" ? b.PersistenceRatio : null;
                if (ar !== null && br !== null && ar !== br) return br - ar;
                if (ar !== null && br === null) return -1;
                if (ar === null && br !== null) return 1;
                const aw = a.WeeksObserved ?? 0;
                const bw = b.WeeksObserved ?? 0;
                if (bw !== aw) return bw - aw;
                const as = a.LatestScore ?? 0;
                const bs = b.LatestScore ?? 0;
                if (bs !== as) return bs - as;
                return (a.StableTrackName || "").localeCompare(b.StableTrackName || "");
            })
            .slice(0, 14);
    }, [tracks]);
    return (
        <section className="space-y-6" data-testid="track-charts">
            <ComparativeScoreChart tracks={tracks} historyByTrackId={historyByTrackId} selectedTrack={selectedTrack} />
            <ActivityHeatmap tracks={heatmapTracks} historyByTrackId={historyByTrackId} allWeeks={allWeeks} onSelectTrack={onSelectTrack} />
            <RoleDistribution summary={summary} tracks={tracks} />
        </section>
    );
};

export default TrackCharts;
